import { ActionBar, Button, UploadFilesButton } from '@components'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { useOnboardSteps, useQueryParam, useQueryParams } from '@hooks'
import { createFolder, IFile, IFolder, uploadFile, UploadFileVariables } from '@query'
import { IFilter } from '@root/components/ActionBar/FilterBar/FilterSelection'
import { TagSelector } from '@root/containers/TagSelector/TagSelector'
import { useAllowUpload } from '@root/hooks/useAllowUpload'
import { setTagsInFilter } from '@root/utils'
import React from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaCloudUploadAlt, FaFolderPlus } from 'react-icons/fa'
import { useMutation, useQueryClient } from '@tanstack/react-query'

interface Props {
  onSearch: (value: string) => void
  onFilter: (filters: any) => void
  activeFilters: IFilter[]
}

export const FilesActionBar: React.FC<Props> = ({ onSearch, onFilter, activeFilters }) => {
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const [folderId] = useQueryParam<number>('folder', value =>
    value ? parseInt(value, 10) : undefined
  )
  const [, setParams] = useQueryParams()
  const { slug, data: business } = useBusinessContext()
  const hasEditPermission = usePermission(PermissionSet.FILES_EDIT)
  const hasUploadPermission = useAllowUpload()
  const { hasOpenAddFileStep, hasOpenAddFolderStep } = useOnboardSteps()

  const filterButtons = [
    {
      id: 'not-used',
      name: t('files.filters.notUsed'),
      filter: { is_used: false }
    }
  ]

  const filterInputs = React.useMemo(
    () => [
      ...(business?.has_tags
        ? [
            <TagSelector
              label={t('components.tagFilterSelector.label')}
              key={'tags_filter_selector'}
              value={activeFilters.filter(value => 'tags' in value).map(value => value.tags)[0]}
              onChange={(tagIds: number[]) => {
                const updatedFilters = setTagsInFilter(activeFilters, tagIds)
                onFilter(updatedFilters)
              }}
            />
          ]
        : [])
    ],
    [activeFilters]
  )

  const { mutate: createBlankFolder, isPending: isCreatingFolder } = useMutation<IFolder>({
    mutationFn: () =>
      createFolder({ slug }, { name: t('files.actionBar.defaultName'), parent: folderId }),
    ...{
      onSuccess: data => {
        queryClient.invalidateQueries({ queryKey: [slug, 'folders'] })
        if (hasOpenAddFolderStep) {
          queryClient.invalidateQueries({ queryKey: [slug], exact: true })
        }
        setParams({
          id: data.id,
          idType: 'folder'
        })
      }
    }
  })

  const { mutateAsync: uploadFileMutation, isPending: isUploading } = useMutation<
    IFile,
    unknown,
    UploadFileVariables
  >({
    mutationFn: ({ file, ...options }) =>
      uploadFile(file, { slug, fileName: file.name, folderId }, options),
    ...{
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [slug, 'files'] })
        if (hasOpenAddFileStep) {
          await queryClient.invalidateQueries({ queryKey: [slug], exact: true })
        }
      },
      onError: error => {
        const { status } = (error as any) ?? {}
        if (status == 426) {
          toast.error(t('error.upgradeRequired'))
        }
      }
    }
  })

  const primaryButtons = [
    ...(hasUploadPermission
      ? [
          <UploadFilesButton
            key="new-file"
            icon={<FaCloudUploadAlt size={16} />}
            uploadFile={(file, options) => uploadFileMutation({ file, ...options })}
            enableAttachmentNotes
            showSpinner={isUploading}
            disabled={isUploading}
          >
            {t('files.actionBar.newFile')}
          </UploadFilesButton>
        ]
      : []),

    ...(hasEditPermission
      ? [
          <Button
            key="new-folder"
            icon={<FaFolderPlus size={16} />}
            showSpinner={isCreatingFolder}
            onClick={() => createBlankFolder()}
          >
            {t('files.actionBar.newFolder')}
          </Button>
        ]
      : [])
  ]

  return (
    <ActionBar
      searchPlaceholder={t('files.actionBar.search')}
      onSearch={search => onSearch(search)}
      onFilter={filters => onFilter(filters)}
      filterButtons={filterButtons}
      filterInputs={filterInputs}
      activeFilters={activeFilters}
      primaryButtons={primaryButtons}
    />
  )
}
